import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { forgotPassword, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const r = await forgotPassword(form.email);
        setForgotSent(true);
        toast.success(r.message);
      } else if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Tekrar hoş geldin");
        navigate("/hesap");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Aramıza hoş geldin");
        navigate("/hesap");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full min-h-[52px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  return (
    <>
      <Seo title={mode === "login" ? "Giriş Yap" : mode === "register" ? "Hesap Oluştur" : "Şifre Yenileme"} path="/giris"  noindex />
      <div className="container-shule py-14 lg:py-24 flex justify-center">
        <Reveal className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/assets/img/logo.png" alt="Shule — Her Anında Sen" className="h-16 w-auto mx-auto" />
          </div>
          <div className="rounded-[2rem] border border-shule-line bg-shule-paper p-6 sm:p-8 shadow-[0_10px_30px_-10px_rgba(61,46,40,0.05)]">
            {mode !== "forgot" && (
              <div data-testid="auth-tabs" className="grid grid-cols-2 gap-1 rounded-full bg-shule-cream p-1 mb-7">
                {[
                  { id: "login", label: "Giriş Yap" },
                  { id: "register", label: "Hesap Oluştur" },
                ].map((t) => (
                  <button key={t.id} data-testid={`auth-tab-${t.id}`} onClick={() => { setMode(t.id); setError(""); }}
                    className={`min-h-[44px] rounded-full text-sm font-semibold transition-colors ${mode === t.id ? "bg-shule-coffee text-shule-paper" : "text-shule-mocha"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {mode === "forgot" ? (
              forgotSent ? (
                <div data-testid="forgot-success" className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-shule-gold" />
                  <h2 className="mt-4 font-serif text-2xl">Bağlantı gönderildi</h2>
                  <p className="mt-3 text-sm text-shule-mocha leading-relaxed">
                    Bu e-postayla bir hesabın varsa, şifre yenileme bağlantısı az önce yola çıktı. Bağlantı 1 saat geçerli.
                  </p>
                  <button data-testid="forgot-back-to-login" onClick={() => { setMode("login"); setForgotSent(false); }}
                    className="btn-outline-shule mt-6 w-full">
                    Girişe Dön
                  </button>
                </div>
              ) : (
                <form data-testid="forgot-form" onSubmit={submit} className="space-y-4">
                  <h2 className="font-serif text-2xl">Şifreni mi unuttun?</h2>
                  <p className="text-sm text-shule-mocha leading-relaxed">
                    E-postanı yaz, sana yenileme bağlantısı gönderelim.
                  </p>
                  <div>
                    <label htmlFor="forgot-email" className="text-sm font-semibold block mb-2">E-posta</label>
                    <input id="forgot-email" data-testid="forgot-email-input" type="email" required className={inputCls}
                      value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ornek@eposta.com" />
                  </div>
                  {error && <p data-testid="forgot-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>}
                  <button data-testid="forgot-submit-button" disabled={loading} className="btn-primary-shule w-full !min-h-[52px] disabled:opacity-40">
                    {loading ? "Gönderiliyor…" : "Bağlantı Gönder"}
                  </button>
                  <button type="button" data-testid="forgot-cancel-button" onClick={() => setMode("login")}
                    className="w-full text-sm font-semibold text-shule-soft hover:text-shule-goldhover min-h-[44px]">
                    ← Girişe dön
                  </button>
                </form>
              )
            ) : (
              <form data-testid="auth-form" onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <label htmlFor="auth-name" className="text-sm font-semibold block mb-2">Ad Soyad</label>
                    <input id="auth-name" data-testid="auth-name-input" required minLength={2} className={inputCls}
                      value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Adın Soyadın" />
                  </div>
                )}
                <div>
                  <label htmlFor="auth-email" className="text-sm font-semibold block mb-2">E-posta</label>
                  <input id="auth-email" data-testid="auth-email-input" type="email" required className={inputCls}
                    value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ornek@eposta.com" />
                </div>
                <div>
                  <label htmlFor="auth-password" className="text-sm font-semibold block mb-2">Şifre</label>
                  <input id="auth-password" data-testid="auth-password-input" type="password" required minLength={6} className={inputCls}
                    value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="En az 6 karakter" />
                </div>
                {error && <p data-testid="auth-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>}
                <button data-testid="auth-submit-button" disabled={loading} className="btn-primary-shule w-full !min-h-[52px] disabled:opacity-40">
                  {loading ? "Bir saniye…" : mode === "login" ? "Giriş Yap" : "Hesabımı Oluştur"}
                </button>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }}
                    className="w-full text-center text-sm font-semibold text-shule-goldhover hover:underline underline-offset-4 min-h-[44px]">
                    Şifremi unuttum
                  </button>
                )}
              </form>
            )}
            <p className="mt-5 text-xs text-shule-soft text-center leading-relaxed">
              Favorilerin ve tercihlerin hesabınla tüm cihazlarında senkronize olur.
            </p>
          </div>
          <p className="mt-6 text-center text-sm text-shule-soft">
            Alışverişe devam etmek ister misin? <Link to="/" className="font-semibold text-shule-goldhover hover:underline underline-offset-4">Ana Sayfa</Link>
          </p>
        </Reveal>
      </div>
    </>
  );
}
